import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ChartTooltip, formatCurrency, formatDateForGranularity } from './chartHelpers';

// Stable references for recharts props — avoids creating new objects on every
// render so child components only re-render when actual data changes.
const AXIS_TICK = { fontSize: 12 };
const AXIS_STROKE = '#94a3b8';
const GRID_STROKE = '#e2e8f0';
const LINE_DOT = { fill: '#f59e0b', strokeWidth: 0, r: 4 };
const BAR_RADIUS = [4, 4, 0, 0];

const EmptyState = ({ message }) => (
  <div className="flex items-center justify-center h-64 text-slate-500">{message}</div>
);

const ChartCard = ({ title, children }) => (
  <Card className="border-slate-200">
    <CardHeader className="pb-2">
      <CardTitle className="text-lg">{title}</CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

export const RevenueChart = ({ data, granularity }) => {
  if (!data || data.length === 0) {
    return (
      <ChartCard title="Revenue Trend">
        <EmptyState message="No revenue data available for this period" />
      </ChartCard>
    );
  }
  return (
    <ChartCard title="Revenue Trend">
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="colorBilled" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorPaid" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
          <XAxis
            dataKey="date"
            tickFormatter={(v) => formatDateForGranularity(v, granularity)}
            tick={AXIS_TICK}
            stroke={AXIS_STROKE}
          />
          <YAxis tickFormatter={formatCurrency} tick={AXIS_TICK} stroke={AXIS_STROKE} />
          <Tooltip content={<ChartTooltip granularity={granularity} />} />
          <Legend />
          <Area type="monotone" dataKey="billed" name="Billed" stroke="#6366f1" fill="url(#colorBilled)" strokeWidth={2} />
          <Area type="monotone" dataKey="paid" name="Collected" stroke="#22c55e" fill="url(#colorPaid)" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export const ClaimsChart = ({ data, granularity }) => {
  if (!data || data.length === 0) {
    return (
      <ChartCard title="Claims Volume">
        <EmptyState message="No claims data available for this period" />
      </ChartCard>
    );
  }
  return (
    <ChartCard title="Claims Volume">
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
          <XAxis
            dataKey="date"
            tickFormatter={(v) => formatDateForGranularity(v, granularity)}
            tick={AXIS_TICK}
            stroke={AXIS_STROKE}
          />
          <YAxis yAxisId="left" tick={AXIS_TICK} stroke={AXIS_STROKE} />
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={formatCurrency}
            tick={AXIS_TICK}
            stroke={AXIS_STROKE}
          />
          <Tooltip content={<ChartTooltip granularity={granularity} />} />
          <Legend />
          <Bar yAxisId="left" dataKey="total_claims" name="Claims" fill="#6366f1" radius={BAR_RADIUS} />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="total_billed"
            name="Billed Amount"
            stroke="#f59e0b"
            strokeWidth={2}
            dot={LINE_DOT}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export const StatusChart = ({ data, granularity }) => {
  if (!data || data.length === 0) {
    return (
      <ChartCard title="Claims by Status">
        <EmptyState message="No status data available for this period" />
      </ChartCard>
    );
  }
  return (
    <ChartCard title="Claims by Status">
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
          <XAxis
            dataKey="date"
            tickFormatter={(v) => formatDateForGranularity(v, granularity)}
            tick={AXIS_TICK}
            stroke={AXIS_STROKE}
          />
          <YAxis tick={AXIS_TICK} stroke={AXIS_STROKE} />
          <Tooltip content={<ChartTooltip granularity={granularity} />} />
          <Legend />
          <Bar dataKey="pending" name="Pending" stackId="a" fill="#f59e0b" />
          <Bar dataKey="paid" name="Paid" stackId="a" fill="#22c55e" />
          <Bar dataKey="denied" name="Denied" stackId="a" fill="#ef4444" radius={BAR_RADIUS} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};
